/**
 * 认证上下文
 * 
 * 提供用户认证状态管理，包括：
 * - 用户登录状态
 * - 登录/登出方法
 * - 密码重置
 * - Session 持久化（6个月）
 */

import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import type { User, Session, AuthError } from '@supabase/supabase-js'

// 认证上下文类型定义
interface AuthContextType {
  // 状态
  user: User | null
  session: Session | null
  isLoading: boolean
  isAuthenticated: boolean
  
  // 方法
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>
  signOut: () => Promise<void>
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>
}

// 创建上下文
const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Provider 组件
interface AuthProviderProps {
  children: ReactNode
}

/**
 * 格式化认证错误为中文消息
 */
const formatAuthError = (error: AuthError): string => {
  const message = error.message || ''
  
  if (message.includes('Invalid login credentials')) {
    return '邮箱或密码错误'
  }
  if (message.includes('Password should be at least')) {
    return '密码至少需要6个字符'
  }
  if (message.includes('User not found')) {
    return '用户不存在'
  }
  if (message.includes('Email not confirmed')) {
    return '邮箱未验证'
  }
  if (message.includes('Invalid email')) {
    return '邮箱格式不正确'
  }
  if (message.includes('Network')) {
    return '网络连接失败，请检查网络'
  }
  
  return message || '认证失败，请重试'
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // 初始化：检查现有 session
  useEffect(() => {
    // 获取当前 session
    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession()
        
        if (currentSession) {
          setSession(currentSession)
          setUser(currentSession.user)
        }
      } catch (error) {
        console.error('初始化认证状态失败:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initializeAuth()

    // 监听认证状态变化
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log('认证状态变化:', event, newSession ? '有session' : '无session')
        
        // 根据事件类型处理状态更新
        switch (event) {
          case 'SIGNED_IN':
          case 'TOKEN_REFRESHED':
          case 'USER_UPDATED':
            // 这些事件应该有有效的 session
            if (newSession) {
              setSession(newSession)
              setUser(newSession.user)
            }
            break
          
          case 'SIGNED_OUT':
            // 登出时清除状态
            setUser(null)
            setSession(null)
            break
          
          case 'INITIAL_SESSION':
            // 初始化 session，可能有也可能没有
            setSession(newSession)
            setUser(newSession?.user ?? null)
            break
          
          case 'PASSWORD_RECOVERY':
            console.log('密码恢复模式')
            // 密码恢复时保持当前状态
            if (newSession) {
              setSession(newSession)
              setUser(newSession.user)
            }
            break
          
          default:
            // 其他事件，只在有 session 时更新
            if (newSession) {
              setSession(newSession)
              setUser(newSession.user)
            }
        }
      }
    )

    // 清理订阅
    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // 登录方法
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      })

      if (error) {
        return { error: { ...error, message: formatAuthError(error) } as AuthError }
      }

      return { error: null }
    } catch (error) {
      console.error('登录失败:', error)
      return { 
        error: { 
          message: '登录失败，请重试',
          name: 'AuthApiError',
          status: 500,
        } as AuthError 
      }
    }
  }, [])

  // 登出方法
  const signOut = useCallback(async () => {
    try {
      await supabase.auth.signOut()
    } catch (error) {
      console.error('登出失败:', error)
    }
  }, [])

  // 请求重置密码
  const resetPassword = useCallback(async (email: string) => {
    try {
      // 获取当前页面的基础 URL
      const baseUrl = window.location.origin
      const redirectTo = `${baseUrl}/reset-password`
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      })

      if (error) {
        return { error: { ...error, message: formatAuthError(error) } as AuthError }
      }

      return { error: null }
    } catch (error) {
      console.error('请求重置密码失败:', error)
      return { 
        error: { 
          message: '发送重置邮件失败，请重试',
          name: 'AuthApiError',
          status: 500,
        } as AuthError 
      }
    }
  }, [])

  // 更新密码
  const updatePassword = useCallback(async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password,
      })

      if (error) {
        return { error: { ...error, message: formatAuthError(error) } as AuthError }
      }

      return { error: null }
    } catch (error) {
      console.error('更新密码失败:', error)
      return { 
        error: { 
          message: '更新密码失败，请重试',
          name: 'AuthApiError',
          status: 500,
        } as AuthError 
      }
    }
  }, [])

  // 计算是否已认证
  const isAuthenticated = !!user && !!session

  const value: AuthContextType = {
    user,
    session,
    isLoading,
    isAuthenticated,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

// Hook：使用认证上下文
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  
  if (context === undefined) {
    throw new Error('useAuth 必须在 AuthProvider 内部使用')
  }
  
  return context
}

export default AuthContext

